import { isDefinedError, ORPCError, safe } from "@orpc/client";
import { TenantSlug } from "@repo/config/sharedConfig";
import {
	CollectionName,
	Patch,
	PatchRemoveOp,
	PatchSetOp,
	QueryResults,
	ReconnectError,
	RemoteApi,
	TandemClient,
	TransientNetworkError,
} from "@repo/tandem";
import { useAsyncEffect } from "@repo/utils/hooks/useAsyncEffect";
import {
	createNamedContext,
	useRequiredContext,
} from "@repo/utils/hooks/useRequiredContext";
import { useStableCallback } from "@repo/utils/hooks/useStableCallback";
import { randomId } from "@repo/utils/randomId";
import { tag } from "@repo/utils/typeUtils";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useApiClient } from "../contexts/ApiContext";
import { getTenantSlug } from "../tenant";
import {
	Database,
	DatabaseSchema,
	QueryBuilder,
	schemaCodecs,
	Transaction,
} from "./schema";

// Helper function to detect transient network errors
function isTransientNetworkError(error: unknown): boolean {
	// ORPC errors with retryable HTTP status codes
	if (
		error instanceof ORPCError &&
		[408, 429, 502, 503, 504].includes(error.status)
	) {
		return true;
	}

	// Check for network-related errors from fetch
	if (error instanceof Error) {
		const message = error.message.toLowerCase();
		// Timeout errors
		if (
			error.name === "TimeoutError" ||
			message.includes("timed out") ||
			message.includes("timeout")
		) {
			return true;
		}
		// Network failure errors (but not user abort)
		if (
			error.name === "TypeError" &&
			(message.includes("fetch") ||
				message.includes("network") ||
				message.includes("load failed"))
		) {
			return true;
		}
	}

	return false;
}

/**
 * Login redirects must not trip the fatal sync page.
 *
 * WRONG_TENANT is not listed: ApiContext signs out and hangs the fetch so
 * nothing reaches here.
 */
function isHandledClientRedirect(error: unknown): boolean {
	return (
		error instanceof ORPCError &&
		(error.status === 401 ||
			error.status === 403 ||
			error.code === "UNAUTHENTICATED" ||
			error.code === "FORBIDDEN")
	);
}

const DatabaseContext = createNamedContext<Database | undefined>(
	"DatabaseContext",
	undefined,
);

const TANDEM_CLIENT_ID_STORAGE_KEY = "tandem.clientId";

/**
 * One id per Demo browser tab. React Strict Mode remounts TandemClient, and
 * Reset reloads the page; a new id each time would look like another live
 * Demo session for ~30 seconds. Other tenants keep minting a fresh id.
 */
function getTandemClientId(): string | undefined {
	if (getTenantSlug() !== TenantSlug.Demo) {
		return undefined;
	}

	try {
		const stored = sessionStorage.getItem(TANDEM_CLIENT_ID_STORAGE_KEY);
		if (stored) {
			return stored;
		}
		const created = randomId();
		sessionStorage.setItem(TANDEM_CLIENT_ID_STORAGE_KEY, created);
		return created;
	} catch {
		return randomId();
	}
}

const SyncErrorContext = createNamedContext<
	{ hasFatalError: boolean } | undefined
>("SyncErrorContext", undefined);

export function DatabaseProvider({ children }: { children: ReactNode }) {
	const api = useApiClient();
	const [hasFatalError, setHasFatalError] = useState(false);

	const db = useMemo(() => {
		// const storage = new IndexedDbTupleStorage<
		// 	DatabaseSchema,
		// 	StorageDatabaseSchema
		// >({
		// 	dbName: "saffron-health",
		// 	codecs: schemaCodecs,
		// });

		const remote: RemoteApi<DatabaseSchema> = {
			async connect({ clientId, poke }) {
				// Use the new sync connect endpoint
				await api.sync.connect({ clientId });

				const intervalId = setInterval(() => {
					poke();
				}, 5000);

				return async () => {
					await clearInterval(intervalId);
				};
			},

			async pull({ clientId, scanWindow, cookie }) {
				const [error, result] = await safe(
					api.sync.pull({
						clientId,
						scanWindow,
						cookie,
					}),
				);

				if (isDefinedError(error) && error.code === "RECONNECT_ERROR") {
					throw new ReconnectError();
				} else if (error) {
					// Throw TransientNetworkError for retryable errors
					if (isTransientNetworkError(error)) {
						throw new TransientNetworkError(
							`Pull failed: ${error instanceof Error ? error.message : String(error)}`,
						);
					}

					// Auth errors already send the user to login or sign them
					// out. A fatal sync error would flash /error before that
					// navigation completes.
					if (!isHandledClientRedirect(error)) {
						setHasFatalError(true);
					}
					throw error;
				}

				// Process the patch to decode entities from storage format to runtime format
				const processedPatch: Patch<DatabaseSchema> = {};

				try {
					// Process set operations
					for (const op of result.patch.set || []) {
						// Decode the entity using the appropriate codec
						const collectionName = op.collection as keyof typeof schemaCodecs;
						const codec = schemaCodecs[collectionName];
						const decodedValue = codec.decode(op.value);
						processedPatch.set ||= [];
						processedPatch.set.push({
							collection: collectionName,
							value: decodedValue,
						} as PatchSetOp<DatabaseSchema>);
					}

					// Process remove operations - decode IDs to proper types
					for (const op of result.patch.remove || []) {
						const collectionName = op.collection;
						processedPatch.remove ||= [];
						processedPatch.remove.push({
							collection: collectionName,
							id: tag(op.id as string),
						} as PatchRemoveOp<DatabaseSchema>);
					}
				} catch (error) {
					// A value this tab's codecs don't recognise means it is running older
					// code than the server — `codec.enum` throws rather than falling back,
					// so a value added in a later release lands here. Send the user to the
					// refresh screen instead of failing silently: this throw is not
					// transient, so SyncEngine's catch notifies nobody, and the cookie has
					// not advanced, so the next poll refetches the same op and throws
					// again. Without this the tab polls forever, never updates, and shows
					// no sign anything is wrong.
					setHasFatalError(true);
					throw error;
				}

				return {
					cookie: result.cookie,
					patch: processedPatch,
					lastMutationId: result.lastMutationId,
				};
			},

			async push({ clientId, mutations }) {
				const encodedMutations = mutations.map(({ id, ops }) => ({
					id,
					ops: ops.map((op) => {
						if (op.type === "remove") return op;
						const codec = schemaCodecs[op.collection];
						return {
							...op,
							// Type assertion needed: op.collection and op.value are correlated
							// by the mutation type, but TypeScript can't prove this statically
							value: codec.encode(op.value as never),
						};
					}),
				}));

				// Use the new sync push endpoint
				const [error] = await safe(
					api.sync.push({ clientId, mutations: encodedMutations }),
				);

				if (isDefinedError(error) && error.code === "RECONNECT_ERROR") {
					throw new ReconnectError();
				} else if (error) {
					// Throw TransientNetworkError for retryable errors
					if (isTransientNetworkError(error)) {
						throw new TransientNetworkError(
							`Push failed: ${error instanceof Error ? error.message : String(error)}`,
						);
					}

					if (!isHandledClientRedirect(error)) {
						setHasFatalError(true);
					}
					throw error;
				}
			},
		};

		// Track active toast ID and connection state to avoid showing duplicate toasts
		let retryToastId: string | number | undefined;
		let hasShownMaxRetriesError = false;

		const persistedClientId = getTandemClientId();
		const db = new TandemClient<DatabaseSchema>({
			remote,
			autoConnect: false,
			...(persistedClientId ? { clientId: tag(persistedClientId) } : {}),
			// minLogLevel: "info",
			onTransientError: ({ retryCount, maxRetries }) => {
				// Don't show retry toasts if we've already shown the max retries error
				if (hasShownMaxRetriesError) return;

				// Show a toast notification for transient errors
				const isReconnecting = retryCount === 1;
				const isRetrying = retryCount > 1;

				if (isReconnecting) {
					// Dismiss any existing toast and show new one
					if (retryToastId) toast.dismiss(retryToastId);
					retryToastId = toast.loading("Connection lost. Reconnecting...", {
						duration: Infinity,
					});
				} else if (isRetrying) {
					// Update existing toast with retry count
					if (retryToastId) toast.dismiss(retryToastId);
					retryToastId = toast.loading(
						`Retrying connection... (${retryCount}/${maxRetries})`,
						{
							duration: Infinity,
						},
					);
				}
			},
			onTransientErrorRecovered: () => {
				// Reset the max retries flag when connection is recovered
				hasShownMaxRetriesError = false;

				// Dismiss the loading toast and show success
				if (retryToastId) {
					toast.dismiss(retryToastId);
					retryToastId = undefined;
				}
				toast.success("Connection restored", {
					duration: 2000,
				});
			},
			onMaxRetriesExceeded: () => {
				// Only show the error toast once
				if (hasShownMaxRetriesError) return;
				hasShownMaxRetriesError = true;

				// Dismiss the loading toast
				if (retryToastId) {
					toast.dismiss(retryToastId);
					retryToastId = undefined;
				}
				// Show error toast with retry action and store the toast ID
				retryToastId = toast.error("Cannot connect to server", {
					duration: Infinity,
					action: {
						label: "Retry",
						onClick: () => {
							hasShownMaxRetriesError = false;
							// Dismiss the error toast before retrying
							if (retryToastId) {
								toast.dismiss(retryToastId);
								retryToastId = undefined;
							}
							void db.connect();
						},
					},
				});
			},
		});

		return db;
	}, [api]);

	useAsyncEffect(async () => {
		await db.connect();
		// @ts-expect-error - for debugging
		window.db = db;

		return async () => {
			// @ts-expect-error - for debugging
			window.db = undefined;
			await db.disconnect();
		};
	}, [db]);

	return (
		<DatabaseContext.Provider value={db}>
			<SyncErrorContext.Provider value={{ hasFatalError }}>
				{children}
			</SyncErrorContext.Provider>
		</DatabaseContext.Provider>
	);
}

export function useDatabase() {
	return useRequiredContext(DatabaseContext);
}

export function useSyncError() {
	return useRequiredContext(SyncErrorContext);
}

export function useTransaction<Args extends any[]>(
	callback: (tx: Transaction, ...args: Args) => void,
): (...args: Args) => void {
	const db = useDatabase();
	const stableCallback = useStableCallback(callback);

	return useCallback(
		(...args: Args) => {
			const tx = db.transact();
			stableCallback(tx, ...args);
			return db.commit(tx);
		},
		[db, stableCallback],
	);
}

export function useQuery<
	Collection extends CollectionName<DatabaseSchema>,
	Query extends QueryBuilder<Collection>,
>(
	collection: Collection,
	queryFn: (q: QueryBuilder<Collection>) => Query,
	deps: React.DependencyList,
): QueryResults<Query>;
export function useQuery<
	Collection extends CollectionName<DatabaseSchema>,
	Query extends QueryBuilder<Collection>,
>(
	collection: Collection,
	queryFn: ((q: QueryBuilder<Collection>) => Query) | undefined,
	deps: React.DependencyList,
): QueryResults<Query> | undefined;
export function useQuery<
	Collection extends CollectionName<DatabaseSchema>,
	Query extends QueryBuilder<Collection>,
>(
	collection: Collection,
	queryFn: ((q: QueryBuilder<Collection>) => Query) | undefined,
	deps: React.DependencyList,
): QueryResults<Query> | undefined {
	const db = useDatabase();
	const [value, setValue] = useState<QueryResults<Query> | undefined>(() =>
		queryFn ? db.run(collection, queryFn) : undefined,
	);

	useEffect(() => {
		if (!queryFn) return;

		const { destroy, result } = db.subscribe(
			collection,
			(db) => queryFn(db),
			(result) => setValue(result),
		);

		setValue(result);

		return destroy;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [db, collection, ...deps]);

	return value;
}

export function useEntity<Collection extends CollectionName<DatabaseSchema>>(
	collection: Collection,
	id: DatabaseSchema[Collection]["id"] | undefined,
): DatabaseSchema[Collection] | undefined {
	const list = useQuery(collection, id ? (q) => q.id(id) : undefined, [id]);
	return list?.[0];
}
